import Select from 'react-select';
import { CustomStyle } from '../CustomSelectStyle';

import { CSSProperties, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAppContext } from '../../contexts/auth';
import useApiCall from '../../hooks/useApiCall';
import useGetGeneralAppData from '../../hooks/useGetGeneralAppData';
import CompanyService from '../../services/CompanyService';
import Loader from '../Loader';
import { CompanyFromApiType, GetCompanyDataResponseType } from './types';

interface AppHeaderInterface {
  isCompanySelectedDisabled: boolean,
}

interface CompaniesSelectOptions {
  label: string;
  options: {
      label: string;
      value: number;
      type: 'M' | 'F';
      associatedMatriz?: string;
  }[];
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function AppHeader({ isCompanySelectedDisabled }: AppHeaderInterface) {
  const [isLoading, setIsLoading] = useState(false);

  const { changeCompany } = useAppContext();
  const { companyCode, companyName, userCode, userProfile, usersCompanyCode, companiesList, setCompaniesList } = useGetGeneralAppData();
  const { apiCall } = useApiCall();

  const getCompaniesList = useCallback(async () => {
    const groupCompanies = (companies: CompanyFromApiType[]) => {
      // Mapeia as empresas matrizes
      const matrizMap = new Map<number, { label: string; options: { label: string; value: number, type: 'M' | 'F', associatedMatriz?: string }[] }>();

      companies.forEach((company) => {
        // Para matriz (M) a chave do grupo é o seu próprio codEmpresa (é nele que as filiais se agrupam via codMatriz).
        // Para filial (F) a chave é o codMatriz (que aponta para o codEmpresa da matriz).
        // Usar codMatriz para matriz quebraria o agrupamento quando a filial aparece antes da matriz na lista,
        // pois a matriz sobrescreveria o grupo criado pela filial.
        const groupKey = company.tipoEmp === 'M' ? company.codEmpresa : (company.codMatriz || company.codEmpresa);
        const matrizGroup = matrizMap.get(groupKey);

        if (company.tipoEmp === 'M') {
          if (matrizGroup) {
            matrizGroup.label = `${company.codEmpresa} - ${company.nomeFantasia} | ${company.cnpj}`;
            matrizGroup.options.unshift({ label: `${company.codEmpresa} - ${company.nomeFantasia} | ${company.cnpj}`, value: company.codEmpresa, type: 'M', associatedMatriz: company.nomeFantasia });
          } else {
            matrizMap.set(company.codEmpresa, {
              label: `${company.codEmpresa} - ${company.nomeFantasia} | ${company.cnpj}`,
              options: [{ label: `${company.codEmpresa} - ${company.nomeFantasia} | ${company.cnpj}`, value: company.codEmpresa, type: 'M', associatedMatriz: company.nomeFantasia }],
            });
          }

        } else if (company.tipoEmp === 'F') {
          if (matrizGroup) {
            matrizGroup.options.push({
              label: `${company.codEmpresa} - ${company.nomeFantasia} | ${company.cnpj}`,
              value: company.codEmpresa,
              type: 'F',
              associatedMatriz: matrizGroup.label,
            });
          } else {
            const correspondingMatriz = companies.find((c) => c.codEmpresa === company.codMatriz);
            matrizMap.set(company.codMatriz, {
              label: `${company.codEmpresa} - ${company.nomeFantasia} | ${company.cnpj}`,
              options: [{ label: `${company.codEmpresa} - ${company.nomeFantasia} | ${company.cnpj}`, value: company.codEmpresa, type: 'F', associatedMatriz: correspondingMatriz?.nomeFantasia }],
            });
          }
        }
      });

      // Converte o Map em um array de grupos
      return Array.from(matrizMap.values());
    };

    if (userProfile < 10) {
      await apiCall({
        apiToCall: CompanyService.getActiveCompaniesList,
        queryParams: { codUsu: userCode, perfilUsu: userProfile, codEmpresa: usersCompanyCode },
        onStartLoad: () => setIsLoading(true),
        onEndLoad: () => setIsLoading(false),
        actionAfterResponse: (response) => {
          if (!response.codigo) {
            toast.error(`Não foi possível recuperar a lista de empresas. Por favor, tente novamente (${response.msg})`);
            return;
          }
          const companies: CompanyFromApiType[] = response.listaEmpresas;
          const mappedCompaniesList = groupCompanies(companies);
          setCompaniesList(mappedCompaniesList ?? []);
        }
      });
    } else {
      await apiCall({
        apiToCall: CompanyService.getCompanyData,
        onStartLoad: () => setIsLoading(true),
        onEndLoad: () => setIsLoading(false),
        queryParams: {
          codEmpresa: usersCompanyCode,
          codUsu: userCode,
          perfilUsu: userProfile,
        },
        actionAfterResponse: (response: GetCompanyDataResponseType) => {
          if (!response.codigo) {
            toast.error(`Não foi possível carregar a lista de filiais. Por favor, tente novamente (${response.msg})`);
            return;
          }
          if (!response.listaEmpresas) {
            setCompaniesList([{ label: companyName, options: [{ label: companyName, value: companyCode, type: 'M' }] }]);
            return;
          }
          const usersCompanyAtList = response.listaEmpresas.find((company) => company.codEmpresa === usersCompanyCode);
          if (usersCompanyAtList?.tipoEmp !== 'M') {
            setCompaniesList([{ label: companyName, options: [{ label: companyName, value: companyCode, type: 'F' }] }]);
            return;
          }
          const companies = response.listaEmpresas;
          const mappedCompaniesList = groupCompanies(companies);
          setCompaniesList(mappedCompaniesList ?? []);
        },
        catchMessage: 'Não foi possível carregar a lista de filiais. Por favor, tente novamente.',
      });
    }

  }, [apiCall, companyCode, companyName, setCompaniesList, userCode, userProfile, usersCompanyCode]);

  const groupStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const groupBadgeStyles: CSSProperties = {
    backgroundColor: '#EBECF0',
    borderRadius: '2em',
    color: '#172B4D',
    display: 'inline-block',
    fontSize: 12,
    fontWeight: 'normal',
    lineHeight: '1',
    minWidth: 1,
    padding: '0.16666666666667em 0.5em',
    textAlign: 'center',
  };

  const formatGroupLabel = (data: CompaniesSelectOptions) => (
    <div style={groupStyles}>
      <span>{data.label}</span>
      <span style={groupBadgeStyles}>{data.options.length}</span>
    </div>
  );

  useEffect(() => {
    getCompaniesList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Loader isLoading={isLoading} />
      <div className="empresaTitle">
        <Select
          value={{ value: companyCode || 0, label: companyName || 'Selecione uma empresa' }}
          options={companiesList}
          onChange={(opt) => {
            changeCompany({ value: opt!.value, label: opt!.label });
          }}
          formatGroupLabel={formatGroupLabel}
          placeholder="Selecione uma empresa"
          styles={CustomStyle}
          isDisabled={isCompanySelectedDisabled}
          classNamePrefix="react-select"
          className="react-select-container"
        />
      </div>
    </>
  );
}
